import { X, Landmark, ArrowLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { SafeHtml } from './SafeHtml';

interface PaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    paypalHtml?: string | null;
    productName?: string;
    productImage?: string;
    productPrice?: string | number;
    productCurrency?: string;
    payType?: string;
    quantity?: number;
}

export function PaymentModal({ isOpen, onClose, paypalHtml, productName, productImage, productPrice, productCurrency, payType, quantity }: PaymentModalProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [showPrexModal, setShowPrexModal] = useState(false);
    const [showBankModal, setShowBankModal] = useState(false);
    const [showPaypalModal, setShowPaypalModal] = useState(true);

    // Reset states when modal opens
    useEffect(() => {
        if (isOpen) {
            setShowPrexModal(false);
            setShowBankModal(false);
            setShowPaypalModal(true);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !paypalHtml || !containerRef.current || !showPaypalModal) return;

        const form = containerRef.current.querySelector('form');
        if (form) {
            form.target = 'paypal_checkout';
            form.addEventListener('submit', () => {
                const width = 450;
                const height = 600;
                const left = (window.screen.width - width) / 2;
                const top = (window.screen.height - height) / 2;

                window.open(
                    '',
                    'paypal_checkout',
                    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
                );
            });
        }

        // Auto-select the option that matches the order quantity
        if (quantity && quantity > 1) {
            const select = containerRef.current.querySelector('select');
            if (select) {
                // Try to find an option whose value equals quantity, or whose text starts with the quantity number
                const targetQty = quantity;
                let matched = false;
                for (const option of Array.from(select.options)) {
                    if (option.value === String(targetQty)) {
                        select.value = option.value;
                        matched = true;
                        break;
                    }
                }
                // Fallback: match by label starting with the quantity number (e.g. "5 Mails : ...")
                if (!matched) {
                    for (const option of Array.from(select.options)) {
                        const label = option.text.trim();
                        if (label.startsWith(`${targetQty} `)) {
                            select.value = option.value;
                            matched = true;
                            break;
                        }
                    }
                }
                // Fallback 2: match by label starting with x{quantity} (e.g. "x5 - 5 GB : ...")
                if (!matched) {
                    for (const option of Array.from(select.options)) {
                        const label = option.text.trim();
                        if (label.startsWith(`x${targetQty} `) || label.startsWith(`x${targetQty}-`) || label.toLowerCase().startsWith(`x${targetQty} -`)) {
                            select.value = option.value;
                            break;
                        }
                    }
                }
                // Trigger change event so PayPal can react if needed
                select.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    }, [isOpen, paypalHtml, quantity]);

    if (!isOpen) return null;

    // Process PayPal HTML to replace the old image button with a modern styled button
    // Also inject styles for PayPal's default elements (dropdowns, tables, etc)
    const processedPaypalHtml = paypalHtml?.replace(
        /<input[^>]*src="https:\/\/www\.paypalobjects\.com\/[^"]*\/btn_[^"]*\.gif"[^>]*>/g,
        `<button type="submit" class="group relative flex items-center justify-center gap-2.5 px-8 py-3 bg-[#0070BA] hover:bg-[#003087] text-white font-bold rounded-full transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-0.5 w-full sm:w-[290px]">
            <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 48 48">
                <path fill="#fff" fill-opacity=".45" d="M16.13 39.115H8.367a1.041 1.041 0 0 1-1.026-1.2l5.234-33.176a1.279 1.279 0 0 1 1.261-1.079h13.335c6.315 0 10.909 4.592 10.8 10.157 3.735 1.95 5.915 5.91 5.234 10.247a12.548 12.548 0 0 1-12.39 10.62H26.89a1.275 1.275 0 0 0-1.261 1.08L23.87 46.9a1.276 1.276 0 0 1-1.262 1.079H15.94a1.04 1.04 0 0 1-1.026-1.199l1.215-7.664v-.002Z"/>
                <path fill="#fff" fill-opacity=".45" d="M37.973 13.817a11.668 11.668 0 0 0-5.441-1.294H21.414a1.277 1.277 0 0 0-1.261 1.08l-2.098 13.293.006-.035a1.28 1.28 0 0 1 1.256-1.042h6.144a12.553 12.553 0 0 0 12.39-10.62c.071-.457.113-.92.122-1.382Z"/>
                <path fill="#fff" d="M16.133 39.115H8.368a1.041 1.041 0 0 1-1.026-1.2l5.234-33.176a1.279 1.279 0 0 1 1.261-1.079h13.335c6.315 0 10.909 4.592 10.801 10.157a11.67 11.67 0 0 0-5.441-1.294H21.414a1.277 1.277 0 0 0-1.261 1.08l-2.098 13.293.006-.035-1.928 12.254Z"/>
            </svg>
            <div class="h-5 w-px bg-white/30 mx-1"></div>
            <span>${payType === 'MENSUAL' || payType === 'ANUAL' ? 'Suscribirse con PayPal' : 'Pagar con PayPal'}</span>
        </button>`
    );

    // Injected styles for PayPal HTML elements like select and tables
    const paypalStyles = `
        <style>
            .paypal-container { width: 100%; display: flex; flex-direction: column; align-items: center; gap: 1.5rem; }
            .paypal-container form { width: 100%; display: flex; flex-direction: column; align-items: center; gap: 1rem; }
            .paypal-container table { border-collapse: separate; border-spacing: 0 0.5rem; margin-bottom: 0.5rem; width: 100%; max-width: 320px; }
            .paypal-container td { padding: 0.5rem 0; color: #374151; font-size: 1.125rem; font-weight: 500; text-align: left; }
            .paypal-container select {
                width: 100%;
                padding: 0.875rem 1.25rem;
                background-color: #f9fafb;
                border: 1px solid #e5e7eb;
                border-radius: 1rem;
                font-size: 1rem;
                color: #111827;
                outline: none;
                transition: all 0.2s;
                cursor: pointer;
                appearance: none;
                background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
                background-repeat: no-repeat;
                background-position: right 1rem center;
                background-size: 1.5em 1.5em;
                box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
            }
            .dark .paypal-container td { color: #e5e7eb; }
            .dark .paypal-container select {
                background-color: #1e293b;
                border-color: #334155;
                color: #f3f4f6;
            }
            .paypal-container select:focus {
                border-color: #3b82f6;
                ring: 2px solid #3b82f6;
            }
            .paypal-container input[type="image"] { display: none; }
            .paypal-container img[src*="pixel"] { display: none; }
            .paypal-container input[type="hidden"] { display: none; }
        </style>
    `;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl dark:bg-slate-800 dark:border dark:border-slate-700 overflow-hidden transform transition-all"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-slate-700">
                    <div className="flex-1 text-center">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                            {showPrexModal ? 'Pagar con Prex' : showBankModal ? 'Transferencia Bancaria' : 'Métodos de Pago'}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-50 rounded-full transition-colors dark:hover:bg-slate-700 dark:text-gray-300 absolute right-4 top-4"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className={(showPrexModal || showBankModal) ? "p-0" : "p-6"}>
                    {showPrexModal ? (
                        <div className="bg-[#6425B2] p-8 flex flex-col items-center justify-center text-white min-h-[400px]">
                            {/* Logo */}
                            <img
                                src="/assets/logos/prex-transparent.svg"
                                alt="Prex"
                                className="h-16 mb-2 object-contain"
                            />

                            {/* Price */}
                            {productPrice && (
                                <h3 className="text-3xl font-bold text-white mb-4">
                                    {productCurrency} {productPrice}
                                </h3>
                            )}

                            {/* Text */}
                            <h4 className="text-2xl font-normal mb-6">Escaneá y pagá</h4>

                            {/* QR */}
                            <div className="bg-white p-3 rounded-xl mb-6 shadow-lg">
                                <img
                                    src="/assets/pagos/QR_Prex.svg"
                                    alt="QR Prex"
                                    className="w-48 h-48 object-contain"
                                />
                            </div>

                            {/* Footer Text */}
                            <div className="text-center text-sm opacity-90 leading-relaxed">
                                <p>Le enviás dinero a:</p>
                                <p className="font-semibold uppercase tracking-wide">LUIS ADRIAN RAINERI ROMERO</p>
                            </div>
                        </div>
                    ) : showBankModal ? (
                        <div className="bg-slate-50 p-6 flex flex-col items-center justify-center dark:bg-slate-800">
                            <div className="w-full max-w-sm bg-white p-6 rounded-xl shadow-sm border border-gray-100 dark:bg-slate-700/50 dark:border-slate-600">
                                {productPrice && (
                                    <div className="text-center mb-6 border-b border-gray-100 dark:border-slate-600 pb-6">
                                        <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                                            {productCurrency} {productPrice}
                                        </h3>
                                    </div>
                                )}
                                <div className="space-y-6 text-center">
                                    {/* Santander Interno */}
                                    <div>
                                        <h4 className="font-bold text-gray-900 dark:text-white mb-2 text-base">Transferencias dentro de Santander</h4>
                                        <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                                            <p className="font-medium">Luis Adrián Raineri</p>
                                            <p>Caja de Ahorro</p>
                                            <p>Cuenta: <span className="font-mono font-medium">5201513407</span></p>
                                            <p>Moneda: USD</p>
                                            <p>Sucursal: 71 - Casa Central</p>
                                        </div>
                                    </div>

                                    <div className="h-px bg-gray-100 dark:bg-slate-600 w-3/4 mx-auto"></div>

                                    {/* Otros Bancos */}
                                    <div>
                                        <h4 className="font-bold text-gray-900 dark:text-white mb-2 text-base">Transferencias desde otros bancos</h4>
                                        <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                                            <p className="font-medium">Santander</p>
                                            <p>Luis Adrián Raineri</p>
                                            <p>Caja de Ahorro</p>
                                            <p>Cuenta: <span className="font-mono font-medium">0071005201513407</span></p>
                                            <p>Moneda: USD</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {productName && (
                                <div className="text-center mb-6">
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Realizar pago de</p>
                                    <div className="flex items-center justify-center gap-3">
                                        {productImage && (
                                            <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center overflow-hidden border border-gray-100 dark:bg-slate-700 dark:border-slate-600">
                                                <img
                                                    src={productImage}
                                                    alt={productName}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        )}
                                        <h4 className="font-bold text-gray-900 dark:text-white text-lg text-left leading-tight">{productName}</h4>
                                    </div>
                                </div>
                            )}

                            {/* Auto Renewal Label */}
                            <div className="flex justify-center items-center mb-6 mt-2">
                                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-full border border-blue-100 dark:border-blue-800/50 shadow-sm">
                                    <div className="relative flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                                    </div>
                                    <span className="text-[11px] font-bold uppercase tracking-widest">Pago de Renovación Automática</span>
                                </div>
                            </div>

                            {processedPaypalHtml ? (
                                <div className="flex flex-col items-center p-4 pt-0 w-full">
                                    <SafeHtml
                                        html={paypalStyles + processedPaypalHtml}
                                        mode="permissive"
                                        ref={containerRef}
                                        className="paypal-container w-full"
                                    />
                                    <p className="mt-4 text-[11px] text-gray-500 dark:text-gray-400 text-center max-w-[280px] leading-relaxed">
                                        Se te cobrará automáticamente al finalizar cada periodo.
                                        Puedes cancelar la suscripción en cualquier momento desde tu panel de PayPal.
                                    </p>
                                </div>
                            ) : (
                                <div className="p-4 border border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer group dark:border-slate-600 dark:hover:border-blue-500 dark:hover:bg-slate-700/50">
                                    <div className="flex items-center gap-3">
                                        <div className="flex-1">
                                            <span className="font-medium text-gray-900 group-hover:text-blue-700 dark:text-white dark:group-hover:text-blue-400">
                                                Botón Paypal HTML (No configurado)
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {payType !== 'MENSUAL' && (
                                <>
                                    {/* Manual Renewal Label */}
                                    <div className="flex justify-center items-center gap-2 mb-2 mt-4">
                                        <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Pago de renovación manual</span>
                                    </div>

                                    <div className="flex justify-center p-4 pt-0">
                                        <button
                                            onClick={() => setShowPrexModal(true)}
                                            className="group relative flex items-center justify-center gap-2.5 px-8 py-3 bg-[#6425B2] hover:bg-[#501e8f] text-white font-bold rounded-full transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-0.5 w-full sm:w-[290px]"
                                        >
                                            <svg className="w-auto h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1600 600">
                                                <g id="Capa_x0020_1">
                                                    <g id="_2929636854336">
                                                        <path fill="#fff" d="M1039.81 309.06l-191.94 0.06c5.16,-22.76 20.13,-40.76 33.07,-51.92 34.13,-29.41 91.9,-29.32 125.88,-0.01 12.38,10.68 28.28,29.46 32.99,51.87zm-291.18 34.06l0 9c0,104.19 88.57,188.76 193.25,188.76 44.79,0 81.67,-9.04 115.07,-30.66 25.43,-16.46 35.85,-27.4 52.66,-49.38 5.11,-6.69 8.38,-12.7 12.77,-20.22l-77.6 -35.1c-5.33,13.95 -23.53,30.62 -35.45,38.04 -58.87,36.6 -147.94,10.07 -162.83,-62.68l288.94 -0.01c1.16,-11.88 3.06,-23.82 2.68,-35.75 0,-107.33 -85.59,-190.5 -193.24,-190.5 -49.86,0 -96.99,16.72 -133.02,48.51 -38.39,33.89 -63.23,87.46 -63.23,139.99z" />
                                                        <path fill="#fff" d="M167.94 147.37c26.04,0 110.02,-2.2 129.78,2.94 32.48,8.43 56.97,35.02 57.18,74.8 0.21,40.03 -25.11,67.13 -57.54,75.54 -19.85,5.15 -103.6,2.97 -129.74,2.97l0.32 -156.25zm-100.06 382.75l0.36 3.52 99.01 -0.02 0.39 -141.74 109.24 0c50.17,0 99.08,-18.1 126.99,-43.76 36.84,-33.86 53.76,-71.06 53.76,-125 0,-69.8 -41.86,-124.86 -104.7,-150.05 -19.26,-7.73 -48.1,-13.95 -71.06,-13.95l-213.99 0 0 471z" />
                                                        <path fill="#fff" d="M1284.75 348.75c-1.23,3.62 -13.28,19.32 -16.65,23.6 -15.31,19.52 -35.67,48.54 -50.82,69.19 -7.21,9.83 -66.4,88.92 -67.03,92.09l109.62 -0.1 71.16 -96.24c2.58,-3.62 6.88,-10.44 9.84,-12.76 3.76,3.25 75.28,108.59 76.56,109.09l114.16 0.01c-0.14,-5.58 -130.77,-179.46 -133.53,-187.51 12.9,-18.73 132.16,-178.03 134.06,-184.25l-109.68 0 -80.51 110.06c-2.7,-2.08 -60.43,-85.78 -67.78,-97.09 -1.7,-2.61 -7.91,-12.36 -10.28,-12.97l-114 0.06 134.88 186.82z" />
                                                        <path fill="#fff" d="M497.5 533.5l93 0.13c1.99,-7.27 0.62,-168.53 0.62,-190.51 0,-34.65 1.21,-54.02 20.77,-74.96 29.96,-32.09 72.43,-31.13 112.88,-20.88 0.01,0 2.39,0.51 2.42,0.52l3.25 -0.36 11.44 -87.04c-33.44,-6.66 -39.59,-7.28 -72.01,-7.28 -38.98,0 -81.63,12.84 -107.06,30.95 -72.95,51.94 -65.69,130.15 -65.69,211.05 0,10.38 -0.81,131.63 0.38,138.38z" />
                                                    </g>
                                                </g>
                                            </svg>
                                            <div className="h-5 w-px bg-white/30 mx-1"></div>
                                            <span>Pagar con Prex</span>
                                        </button>
                                    </div>

                                    <div className="flex justify-center p-4 pt-0">
                                        <button
                                            onClick={() => setShowBankModal(true)}
                                            className="group relative flex items-center justify-center gap-2.5 px-8 py-3 bg-[#1e293b] hover:bg-[#0f172a] text-white font-bold rounded-full transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-0.5 w-full sm:w-[290px]"
                                        >
                                            <Landmark className="w-5 h-5" />
                                            <div className="h-5 w-px bg-white/30 mx-1"></div>
                                            <span>Transferencia Bancaria</span>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-between items-center dark:bg-slate-800/50 dark:border-slate-700">
                    {(showPrexModal || showBankModal) ? (
                        <button
                            onClick={() => {
                                setShowPrexModal(false);
                                setShowBankModal(false);
                            }}
                            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Volver a métodos de pago
                        </button>
                    ) : (
                        <div></div> // Spacer to keep Cancel button on the right
                    )}

                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white transition-colors"
                    >
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    );
}
